// Chip filtro e campo di ricerca della lista ordini (redesign 27/10/2026),
// condivisi da tutte le pagine: spento = bianco col pallino del suo colore,
// acceso = pieno del colore del badge di stato. Il colore arriva in `--tono`
// perche' il JIT di Tailwind non vede colori composti a runtime.
import type { CSSProperties, ReactNode } from "react";
import { BADGE_TONI, type StatoTone } from "@/features/ordini/components/statoToni";

const CHIP =
  "group inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-full border pl-2.5 pr-3 text-[12.5px] font-semibold transition-[background-color,color,border-color,box-shadow,transform] duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 focus-visible:ring-offset-1 max-md:h-10 max-md:shrink-0 max-md:pl-3.5 max-md:pr-4 max-md:text-[14px]";
const ACCESO =
  "border-[color:var(--tono)] bg-[color:var(--tono)] text-white shadow-[inset_0_1px_2px_rgba(31,27,22,0.3)]";
const SPENTO =
  "border-line-2 bg-card text-ink hover:border-[color:var(--tono)] hover:bg-[color:color-mix(in_srgb,var(--tono)_6%,rgb(var(--om-card)))]";

export function ChipFiltro({
  tono,
  attivo,
  onClick,
  children,
  conteggio,
  extra,
  role,
  title,
  evidenzia,
}: {
  tono: StatoTone;
  attivo: boolean;
  onClick: () => void;
  children: ReactNode;
  /** Il numero accanto all'etichetta (righe nella vista). */
  conteggio?: number | string;
  /** Un'etichetta in piu' (allarme), piena del tono. */
  extra?: ReactNode;
  /** «tab» dentro un tablist; altrimenti bottone con aria-pressed. */
  role?: "tab";
  title?: string;
  /** Bordo del tono anche da spento: la vista chiede attenzione. */
  evidenzia?: boolean;
}) {
  return (
    <button
      type="button"
      role={role}
      aria-selected={role === "tab" ? attivo : undefined}
      aria-pressed={role === "tab" ? undefined : attivo}
      title={title}
      onClick={onClick}
      style={{ "--tono": BADGE_TONI[tono] } as CSSProperties}
      className={[
        CHIP,
        attivo ? ACCESO : SPENTO,
        evidenzia && !attivo ? "!border-[color:var(--tono)]" : "",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={`h-2 w-2 shrink-0 rounded-full ${attivo ? "bg-card" : "bg-[color:var(--tono)]"}`}
      />
      {children}
      {conteggio !== undefined && (
        <span
          className={[
            "min-w-[1.25rem] rounded-full px-1.5 text-center text-[11.5px] font-bold tabular-nums",
            attivo ? "bg-white/20" : "bg-paper-2",
          ].join(" ")}
        >
          {conteggio}
        </span>
      )}
      {extra && (
        <span
          className={[
            "rounded-full px-1.5 text-[11.5px] font-bold",
            attivo ? "bg-white/20" : "bg-[color:var(--tono)] text-white",
          ].join(" ")}
        >
          {extra}
        </span>
      )}
    </button>
  );
}

/** Campo di ricerca a pillola con la lente, come quello della lista ordini. */
export function CampoCerca({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  ariaLabel: string;
  className?: string;
}) {
  return (
    <label
      className={`flex h-[34px] min-w-0 items-center gap-2 rounded-full border border-line-2 bg-card px-3.5 text-[13px] text-ink transition-[border-color,box-shadow] focus-within:border-ink-3 focus-within:shadow-[0_0_0_3px_rgba(31,27,22,0.06)] max-md:h-12 max-md:text-[16px] ${className}`}
    >
      <svg
        className="h-[14px] w-[14px] shrink-0 fill-none stroke-current stroke-[1.6] text-ink-3 [stroke-linecap:round] [stroke-linejoin:round]"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="min-w-0 flex-1 border-none bg-transparent outline-none placeholder:text-ink-3"
      />
    </label>
  );
}
